import { t as translate } from 'i18next';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import * as yup from 'yup';

import FormDialog from '@/components/atoms/FormDialog/FormDialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  AccountDebt,
  DebtPaymentEvidence,
  DebtPaymentMethod,
  DebtStatus,
  UpdateDebtPayload,
} from '@/shared/types/contacts';
import { createYupResolver } from '@/util/forms/createYupResolver';

import {
  DEBT_PAYMENT_METHODS,
  DEBT_STATUS_KEY,
  PAYMENT_EVIDENCE_KEY,
  PAYMENT_METHOD_KEY,
} from '../constants';
import { useDebts } from '../hooks/useDebts';

const PAYMENT_REFERENCE_MAX = 128;
const PAYMENT_STATUSES: DebtStatus[] = ['paid', 'settled'];
const DEBT_STATUSES = Object.keys(DEBT_STATUS_KEY) as DebtStatus[];
const EVIDENCES = Object.keys(PAYMENT_EVIDENCE_KEY) as DebtPaymentEvidence[];

const STATUS_OPTION_KEY: Record<DebtStatus, string> = {
  ...DEBT_STATUS_KEY,
  settled: 'contacts.debts.status_option_settled',
};

const EVIDENCE_OPTION_KEY: Record<DebtPaymentEvidence, string> = {
  verified: 'contacts.debts.payment_evidence_option.verified',
  reported: 'contacts.debts.payment_evidence_option.reported',
};

type ChangeStatusForm = {
  status: DebtStatus;
  payment_method: DebtPaymentMethod | '';
  payment_reference: string;
  payment_evidence: DebtPaymentEvidence | '';
};

const schema = yup.object({
  status: yup
    .mixed<DebtStatus>()
    .oneOf(DEBT_STATUSES)
    .required(() => translate('validation.required')),
  payment_method: yup.string(),
  payment_reference: yup
    .string()
    .trim()
    .when('status', {
      is: (status: DebtStatus) => PAYMENT_STATUSES.includes(status),
      then: (schema) =>
        schema.max(PAYMENT_REFERENCE_MAX, () =>
          translate('validation.less_than', { 0: PAYMENT_REFERENCE_MAX })
        ),
    }),
  payment_evidence: yup.string(),
});

const toPayload = (data: ChangeStatusForm): UpdateDebtPayload => {
  const { status, payment_method, payment_reference, payment_evidence } = data;
  if (!PAYMENT_STATUSES.includes(status)) {
    return { status };
  }
  return {
    status,
    ...(payment_method ? { payment_method } : {}),
    ...(payment_reference ? { payment_reference } : {}),
    ...(payment_evidence ? { payment_evidence } : {}),
  };
};

interface ChangeDebtStatusDialogProps {
  debt: AccountDebt;
  onOpenChange: (open: boolean) => void;
}

export const ChangeDebtStatusDialog = ({ debt, onOpenChange }: ChangeDebtStatusDialogProps) => {
  const { t } = useTranslation();
  const { updateDebt } = useDebts();
  const form = useForm<ChangeStatusForm>({
    mode: 'onTouched',
    resolver: createYupResolver<ChangeStatusForm>(schema),
    defaultValues: {
      status: debt.status,
      payment_method: '',
      payment_reference: '',
      payment_evidence: '',
    },
  });
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = form;
  const status = useWatch({ control, name: 'status' });

  const onSubmit = async (data: ChangeStatusForm) => {
    await updateDebt({ debt, payload: toPayload(data) });
    toast.success(t('contacts.debts.status_changed'));
    onOpenChange(false);
  };

  return (
    <FormDialog
      open
      onOpenChange={onOpenChange}
      title={t('contacts.debts.change_status')}
      description={debt.debt_external_id || debt.debt_id}
      onSubmit={handleSubmit(onSubmit)}
      submitDisabled={status === debt.status || isSubmitting}
      isLoading={isSubmitting}
      submitOnEnter
    >
      <Form {...form}>
        <div className="space-y-4">
          <FormField
            control={control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('common.status')}</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger className="w-full" onBlur={field.onBlur}>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {DEBT_STATUSES.map((option) => (
                      <SelectItem key={option} value={option}>
                        {t(STATUS_OPTION_KEY[option])}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {PAYMENT_STATUSES.includes(status) && (
            <fieldset className="space-y-4">
              <legend className="mb-4 text-sm leading-5 font-medium text-muted-foreground">
                {t('contacts.debts.payment_section')}
              </legend>
              <FormField
                control={control}
                name="payment_method"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contacts.debts.payment_method')}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full" onBlur={field.onBlur}>
                          <SelectValue placeholder={t('common.select_dots')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {DEBT_PAYMENT_METHODS.map((method) => (
                          <SelectItem key={method} value={method}>
                            {t(PAYMENT_METHOD_KEY[method])}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="payment_reference"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contacts.debts.payment_reference')}</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="off" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="payment_evidence"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contacts.debts.payment_evidence')}</FormLabel>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="gap-2"
                      >
                        {EVIDENCES.map((evidence) => (
                          <RadioGroupItem
                            key={evidence}
                            value={evidence}
                            id={`payment-evidence-${evidence}`}
                          >
                            {t(EVIDENCE_OPTION_KEY[evidence])}
                          </RadioGroupItem>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </fieldset>
          )}
        </div>
      </Form>
    </FormDialog>
  );
};
